/**
 * Creatives table using @tanstack/react-table.
 * Configurable columns, OR gate filter, pagination, sort, search.
 *
 * FR-71: Column picker with grouped checkboxes
 * FR-72: Core columns always visible (Name, Media Type, Verdict)
 * FR-73: Main/Secondary KPI column headers adapt per template
 * FR-74: Gate filter uses OR logic (not AND)
 * FR-75: Sort by any column click
 * FR-76: Pagination 10 per page
 * FR-77: Drilldown banner when navigated from another tab
 */

import {
  useReactTable,
  getCoreRowModel,
  // getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  createColumnHelper,
  flexRender,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { EmptyState } from '@/components/dashboard/shared/EmptyState';
import { SkeletonCard } from '@/components/dashboard/shared/SkeletonLoaders';
import { useCreativesList } from '@/hooks/useDashboard';
import { fmtKpiValue } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';
import { META_KPI_CATALOG } from '@/types/dashboard';
import type { DashboardCreative } from '@/types/dashboard';

import { ColumnPicker } from './ColumnPicker';
import { DrilldownBanner } from './DrilldownBanner';

// ============================================================================
// CONSTANTS
// ============================================================================

const PAGE_SIZE = 10;

const VERDICT_COLORS: Record<string, string> = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
};

// Always-on column IDs that cannot be hidden (FR-72)
export const ALWAYS_ON_COLUMNS = ['name', 'mediaType', 'verdict'];

// ============================================================================
// HELPERS
// ============================================================================

function VerdictBadge({ verdict }: { verdict: string }) {
  return (
    <span
      className="text-xs px-2 py-0.5 rounded font-medium"
      style={{
        backgroundColor: `${VERDICT_COLORS[verdict] ?? '#8B95A7'}20`,
        color: VERDICT_COLORS[verdict] ?? '#8B95A7',
      }}
    >
      {verdict}
    </span>
  );
}

function MediaBadge({ mediaType }: { mediaType: string }) {
  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-surface-subtle text-secondary font-medium">
      {mediaType === 'video' ? 'VID' : 'IMG'}
    </span>
  );
}

// ============================================================================
// COLUMN BUILDING
// ============================================================================

export interface ColumnGroup {
  category: string;
  columns: { id: string; label: string; alwaysOn?: boolean }[];
}

export function buildColumnGroups(
  template: { kpiConfig?: { primaryKpiId: string | null; secondaryKpiIds: string[] } | null; gates?: { gateId: string; name: string }[]; diagnosisDimensions?: { dimensionId: string; name: string; enabled: boolean }[] } | null
): ColumnGroup[] {
  const groups: ColumnGroup[] = [];

  // Core group
  groups.push({
    category: 'Core',
    columns: [
      { id: 'name', label: 'Name', alwaysOn: true },
      { id: 'mediaType', label: 'Type', alwaysOn: true },
      { id: 'verdict', label: 'Verdict', alwaysOn: true },
      { id: 'projectName', label: 'Project' },
    ],
  });

  // KPIs — split by template kpiConfig (FR-73)
  const primaryId = template?.kpiConfig?.primaryKpiId;
  const secondaryIds = template?.kpiConfig?.secondaryKpiIds ?? [];
  const templateKpiIds = new Set<string>([
    ...(primaryId ? [primaryId] : []),
    ...secondaryIds,
  ]);

  // Template KPIs: primary + secondary (shown prominently)
  const templateKpiCols = META_KPI_CATALOG
    .filter((kpi) => templateKpiIds.has(kpi.id))
    .map((kpi) => ({
      id: `kpi_${kpi.id}`,
      label: kpi.id === primaryId ? `${kpi.label} (Primary)` : kpi.label,
    }));

  // Other KPIs: remaining (available but not default)
  const otherKpiCols = META_KPI_CATALOG
    .filter((kpi) => !templateKpiIds.has(kpi.id))
    .map((kpi) => ({
      id: `kpi_${kpi.id}`,
      label: kpi.label,
    }));

  if (templateKpiCols.length > 0) {
    groups.push({ category: 'KPIs', columns: templateKpiCols });
  }
  if (otherKpiCols.length > 0) {
    groups.push({ category: 'Other KPIs', columns: otherKpiCols });
  }
  // Fallback: if no template kpiConfig, show all KPIs in one group
  if (templateKpiCols.length === 0 && otherKpiCols.length === 0) {
    const allKpiCols = META_KPI_CATALOG.map((kpi) => ({
      id: `kpi_${kpi.id}`,
      label: kpi.label,
    }));
    groups.push({ category: 'KPIs', columns: allKpiCols });
  }

  // Dimensions
  const dimCols = (template?.diagnosisDimensions ?? [])
    .filter((d) => d.enabled)
    .map((d) => ({ id: `dim_${d.dimensionId}`, label: d.name }));
  if (dimCols.length > 0) {
    groups.push({ category: 'Dimensions', columns: dimCols });
  }

  // Gates
  const gateCols = (template?.gates ?? []).map((g) => ({
    id: `gate_${g.gateId}`,
    label: g.name,
  }));
  if (gateCols.length > 0) {
    groups.push({ category: 'Gates', columns: gateCols });
  }

  return groups;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function CreativesTable() {
  const { filters, activeTemplate } = useDashboardContext();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [verdictFilter, setVerdictFilter] = useState<string | null>(
    filters.drilldownVerdict ?? null
  );
  const [mediaTypeFilter, setMediaTypeFilter] = useState<'all' | 'video' | 'image'>(
    filters.mediaType ?? 'all'
  );
  const [selectedGates, setSelectedGates] = useState<Set<string>>(
    filters.drilldownGate ? new Set([filters.drilldownGate]) : new Set()
  );
  const [page, setPage] = useState(0);

  // Default visible columns: core + primary + secondary KPIs
  const primaryKpiId = activeTemplate?.kpiConfig?.primaryKpiId;
  const secondaryKpiIds = activeTemplate?.kpiConfig?.secondaryKpiIds ?? [];

  const defaultVisibleIds = useMemo(() => {
    const ids = new Set(ALWAYS_ON_COLUMNS);
    ids.add('projectName');
    if (primaryKpiId) ids.add(`kpi_${primaryKpiId}`);
    if (secondaryKpiIds[0]) ids.add(`kpi_${secondaryKpiIds[0]}`);
    return ids;
  }, [primaryKpiId, secondaryKpiIds]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const vis: VisibilityState = {};
    // All non-default columns hidden
    for (const kpi of META_KPI_CATALOG) {
      vis[`kpi_${kpi.id}`] = defaultVisibleIds.has(`kpi_${kpi.id}`);
    }
    return vis;
  });

  // Ensure always-on columns are never hidden
  const safeColumnVisibility = useMemo(() => {
    const vis = { ...columnVisibility };
    for (const col of ALWAYS_ON_COLUMNS) {
      vis[col] = true;
    }
    return vis;
  }, [columnVisibility]);

  // Fetch data
  const gatesParam = selectedGates.size > 0 ? Array.from(selectedGates) : undefined;
  const { data, isLoading, isError } = useCreativesList(
    filters,
    page + 1,
    PAGE_SIZE,
    sorting[0]?.id ?? 'verdict',
    sorting[0]?.desc ? 'desc' : 'asc',
    gatesParam,
    searchQuery || undefined
  );

  const creativesData = data;
  const creatives = creativesData?.creatives ?? [];
  // Client-side filters (verdict + media type + OR gate logic)
  const filteredCreatives = useMemo(() => {
    return creatives.filter((c) => {
      if (verdictFilter && c.verdict !== verdictFilter) return false;
      if (mediaTypeFilter !== 'all' && c.mediaType !== mediaTypeFilter) return false;
      if (searchQuery.trim()) {
        if (!c.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      }
      // OR gate filter (FR-74): show creative if it FAILS ANY selected gate
      if (selectedGates.size > 0) {
        const failsAny = Array.from(selectedGates).some((gateId) => {
          return c.gates[gateId] === false;
        });
        if (!failsAny) return false;
      }
      return true;
    });
  }, [creatives, verdictFilter, mediaTypeFilter, searchQuery, selectedGates]);

  // Column groups for picker
  const columnGroups = useMemo(
    () => buildColumnGroups(activeTemplate),
    [activeTemplate]
  );

  // Build TanStack Table columns
  const columnHelper = createColumnHelper<DashboardCreative>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo<ColumnDef<DashboardCreative, any>[]>(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cols: ColumnDef<DashboardCreative, any>[] = [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => (
          <Link
            to={`/creatives/${info.row.original.creativeId}`}
            className="text-xs text-brand hover:underline font-medium"
          >
            {info.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor('mediaType', {
        header: 'Type',
        cell: (info) => <MediaBadge mediaType={info.getValue()} />,
      }),
      columnHelper.accessor('verdict', {
        header: 'Verdict',
        cell: (info) => <VerdictBadge verdict={info.getValue()} />,
      }),
      columnHelper.accessor('projectName', {
        header: 'Project',
        cell: (info) => (
          <span className="text-xs text-secondary">{info.getValue()}</span>
        ),
      }),
    ];

    // KPI columns
    for (const kpi of META_KPI_CATALOG) {
      cols.push(
        columnHelper.accessor((row) => row.kpis[kpi.id], {
          id: `kpi_${kpi.id}`,
          header: () => {
            const isPrimary = kpi.id === primaryKpiId;
            const isSecondary = secondaryKpiIds.includes(kpi.id);
            return (
              <span>
                {kpi.label}
                {isPrimary && <span className="ml-1 text-brand">*</span>}
                {isSecondary && <span className="ml-1 text-muted">.</span>}
              </span>
            );
          },
          cell: (info) => {
            const val = info.getValue() as number | null;
            if (val === null || val === undefined) return <span className="text-xs text-muted">--</span>;
            return <span className="text-xs text-secondary">{fmtKpiValue(val, kpi.unit)}</span>;
          },
        })
      );
    }

    // Dimension columns
    for (const dim of activeTemplate?.diagnosisDimensions ?? []) {
      if (!dim.enabled) continue;
      cols.push(
        columnHelper.accessor((row) => row.dimensions[dim.dimensionId], {
          id: `dim_${dim.dimensionId}`,
          header: dim.name,
          cell: (info) => {
            const val = info.getValue();
            if (val === null || val === undefined) return <span className="text-xs text-muted">--</span>;
            return <span className="text-xs text-secondary truncate block max-w-[120px]">{String(val)}</span>;
          },
        })
      );
    }

    // Gate columns
    for (const gate of activeTemplate?.gates ?? []) {
      cols.push(
        columnHelper.accessor((row) => row.gates[gate.gateId], {
          id: `gate_${gate.gateId}`,
          header: gate.name,
          cell: (info) => {
            const val = info.getValue() as boolean | undefined;
            if (val === undefined || val === null) return <span className="text-xs text-muted">--</span>;
            return (
              <span className={cn('text-xs px-1.5 py-0.5 rounded font-medium', val ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')}>
                {val ? 'Pass' : 'Fail'}
              </span>
            );
          },
        })
      );
    }

    return cols;
  }, [activeTemplate, primaryKpiId, secondaryKpiIds, columnHelper]);

  const table = useReactTable({
    data: filteredCreatives,
    columns,
    state: {
      sorting,
      columnVisibility: safeColumnVisibility,
      pagination: { pageIndex: page, pageSize: PAGE_SIZE },
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false,
  });

  const totalPages = Math.ceil(filteredCreatives.length / PAGE_SIZE);

  // Gate toggle: OR logic
  function toggleGate(gateId: string) {
    setSelectedGates((prev) => {
      const next = new Set(prev);
      if (next.has(gateId)) next.delete(gateId);
      else next.add(gateId);
      return next;
    });
    setPage(0);
  }

  // CSV export
  const csvHeaders = ['Name', 'Type', 'Verdict', 'Project'];
  const csvRows = filteredCreatives.map((c) => [
    c.name,
    c.mediaType,
    c.verdict,
    c.projectName,
  ]);

  if (isLoading) {
    return <SkeletonCard lines={10} />;
  }

  if (isError) {
    return (
      <EmptyState
        title="Failed to load creatives"
        description="Please try refreshing the page."
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Drilldown banner (FR-77) */}
      <DrilldownBanner />

      {/* Filters bar */}
      <div className="rounded-[14px] bg-surface shadow-sm px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <input
            type="text"
            placeholder="Search creatives..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            className="text-xs border border-border-subtle rounded px-2.5 py-1.5 bg-canvas text-primary focus:outline-none focus:ring-1 focus:ring-brand w-44"
          />

          {/* Verdict chips */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted">Verdict:</span>
            {(['Scale', 'Fix', 'Kill'] as const).map((v) => (
              <button
                key={v}
                onClick={() => { setVerdictFilter((prev) => prev === v ? null : v); setPage(0); }}
                className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors border',
                  verdictFilter === v
                    ? 'text-white border-transparent'
                    : 'bg-surface border-border-subtle text-secondary hover:bg-surface-subtle'
                )}
                style={verdictFilter === v ? { backgroundColor: VERDICT_COLORS[v], borderColor: VERDICT_COLORS[v] } : undefined}
              >
                {v}
              </button>
            ))}
          </div>

          {/* Media type filter */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted">Media:</span>
            {(['all', 'video', 'image'] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setMediaTypeFilter(m); setPage(0); }}
                className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors',
                  mediaTypeFilter === m
                    ? 'bg-brand text-white'
                    : 'bg-surface-subtle text-secondary hover:bg-surface-subtle'
                )}
              >
                {m === 'all' ? 'All' : m === 'video' ? 'Video' : 'Image'}
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted">
              {filteredCreatives.length} creative{filteredCreatives.length !== 1 ? 's' : ''}
            </span>
            <ColumnPicker
              columnGroups={columnGroups}
              columnVisibility={safeColumnVisibility}
              onToggleColumn={(colId) => {
                if (ALWAYS_ON_COLUMNS.includes(colId)) return;
                setColumnVisibility((prev) => ({
                  ...prev,
                  [colId]: !prev[colId],
                }));
              }}
            />
            <CsvExport headers={csvHeaders} rows={csvRows} filename="creatives-list" />
          </div>
        </div>

        {/* Gate filter chips (OR logic - FR-74) */}
        {(activeTemplate?.gates ?? []).length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5 pt-2.5 border-t border-border-subtle">
            <span className="text-xs text-muted">Gate failed:</span>
            {(activeTemplate?.gates ?? []).map((gate) => (
              <button
                key={gate.gateId}
                onClick={() => toggleGate(gate.gateId)}
                className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium transition-colors border',
                  selectedGates.has(gate.gateId)
                    ? 'bg-danger/10 text-danger border-danger/30'
                    : 'bg-surface border-border-subtle text-secondary hover:bg-surface-subtle'
                )}
              >
                {gate.name}
                {gate.role === 'critical' && (
                  <span className="ml-1 text-[9px] text-danger/70">crit</span>
                )}
              </button>
            ))}
            {selectedGates.size > 0 && (
              <button
                onClick={() => { setSelectedGates(new Set()); setPage(0); }}
                className="text-xs text-muted hover:text-secondary underline"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-[14px] bg-surface shadow-sm overflow-hidden">
        {filteredCreatives.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted">
            No creatives match the current filters.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="border-b border-border-subtle bg-canvas">
                      <th className="text-left px-4 py-2.5 text-muted font-medium w-8">#</th>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className="text-left px-4 py-2.5 text-muted font-medium cursor-pointer hover:text-primary whitespace-nowrap"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' && ' ^'}
                          {header.column.getIsSorted() === 'desc' && ' v'}
                          {!header.column.getIsSorted() && ' <>'}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, idx) => (
                    <tr
                      key={row.id}
                      className={cn(
                        'border-b border-border-subtle hover:bg-canvas transition-colors',
                        idx % 2 === 1 && 'bg-canvas'
                      )}
                    >
                      <td className="px-4 py-2.5 text-muted">{page * PAGE_SIZE + idx + 1}</td>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-2.5 max-w-[160px]">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination (FR-76) */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-border-subtle">
                <span className="text-xs text-muted">
                  Page {page + 1} of {totalPages} ({filteredCreatives.length} total)
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-2.5 py-1 text-xs rounded border border-border-subtle text-secondary disabled:opacity-40 hover:bg-surface-subtle disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const pageNum = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={cn(
                          'px-2.5 py-1 text-xs rounded border transition-colors',
                          page === pageNum
                            ? 'bg-brand text-white border-brand'
                            : 'border-border-subtle text-secondary hover:bg-surface-subtle'
                        )}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-2.5 py-1 text-xs rounded border border-border-subtle text-secondary disabled:opacity-40 hover:bg-surface-subtle disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
